import type { Position } from "@/types/game.types"
import type { ItemDefinition, ItemRarity, DroppedItem } from "@/types/item.types"
import type { EnemyVariant } from "@/types/enemy.types"
import { itemDefinitions } from "@/data/item-types"
import { createDroppedItem } from "@/systems/item-system"

// ============================================
// LOOT DROP CONFIGURATION
// ============================================

// Base drop chances by rarity (must sum to 100)
export const RARITY_DROP_CHANCES: Record<ItemRarity, number> = {
  common: 64,
  rare: 24,
  epic: 8,
  legendary: 3,
  unique: 1,
}

// Enemy drop chances by variant
export const ENEMY_DROP_CHANCES: Record<EnemyVariant, number> = {
  regular: 3, // 3% chance to drop an item
  elite: 10, // 10% chance to drop an item
  unique: 100, // 100% chance to drop an item
}

// Elite enemies have an additional chance to drop a potion
export const ELITE_POTION_DROP_CHANCE = 33 // 33%

// Unique enemies have a chance to drop an additional item
export const UNIQUE_ADDITIONAL_DROP_CHANCE = 50 // 50%

// Rarity colors for UI display
export const RARITY_COLORS: Record<ItemRarity, string> = {
  common: "#FFFFFF", // White
  rare: "#22C55E", // Green
  epic: "#3B82F6", // Blue
  legendary: "#F97316", // Orange
  unique: "#991B1B", // Dark red
}

// Item spacing when multiple items drop from same enemy
export const ITEM_DROP_SPACING = 28 // pixels between items

// ============================================
// HELPER FUNCTIONS - Get items by category
// ============================================

// Cache for items by rarity
let cachedItemsByRarity: Record<ItemRarity, ItemDefinition[]> | null = null
let cachedPotions: ItemDefinition[] | null = null
let cachedLegendaryOrUnique: ItemDefinition[] | null = null

// Get all items grouped by rarity
export const getItemsByRarity = (): Record<ItemRarity, ItemDefinition[]> => {
  if (!cachedItemsByRarity) {
    cachedItemsByRarity = {
      common: [],
      rare: [],
      epic: [],
      legendary: [],
      unique: [],
    }
    for (const item of Object.values(itemDefinitions)) {
      cachedItemsByRarity[item.rarity].push(item)
    }
  }
  return cachedItemsByRarity
}

// Get all potion items
export const getPotionItems = (): ItemDefinition[] => {
  if (!cachedPotions) {
    cachedPotions = Object.values(itemDefinitions).filter(
      (item) => item.category === "potion"
    )
  }
  return cachedPotions
}

// Get all legendary or unique items (for unique enemy guaranteed drops)
export const getLegendaryOrUniqueItems = (): ItemDefinition[] => {
  if (!cachedLegendaryOrUnique) {
    cachedLegendaryOrUnique = Object.values(itemDefinitions).filter(
      (item) => item.rarity === "legendary" || item.rarity === "unique"
    )
  }
  return cachedLegendaryOrUnique
}

// Get a random potion
export const getRandomPotion = (): ItemDefinition | null => {
  const potions = getPotionItems()
  if (potions.length === 0) return null
  return potions[Math.floor(Math.random() * potions.length)]
}

// Get a random legendary or unique item
export const getRandomLegendaryOrUniqueItem = (): ItemDefinition | null => {
  const items = getLegendaryOrUniqueItems()
  if (items.length === 0) return null
  return items[Math.floor(Math.random() * items.length)]
}

// ============================================
// LOOT ROLL FUNCTIONS
// ============================================

/**
 * Roll for an item rarity based on drop chances
 * @param modifier Optional modifier to adjust drop chances (e.g., 1.5 = 50% better rare+ drops)
 */
export const rollItemRarity = (modifier: number = 1): ItemRarity => {
  const roll = Math.random() * 100

  // Apply modifier to non-common rarities
  // Modifier increases chances for rare/epic/legendary/unique
  const adjustedChances = {
    common: RARITY_DROP_CHANCES.common,
    rare: RARITY_DROP_CHANCES.rare * modifier,
    epic: RARITY_DROP_CHANCES.epic * modifier,
    legendary: RARITY_DROP_CHANCES.legendary * modifier,
    unique: RARITY_DROP_CHANCES.unique * modifier,
  }

  // Normalize to 100%
  const total = Object.values(adjustedChances).reduce((sum, val) => sum + val, 0)
  const normalized = {
    common: (adjustedChances.common / total) * 100,
    rare: (adjustedChances.rare / total) * 100,
    epic: (adjustedChances.epic / total) * 100,
    legendary: (adjustedChances.legendary / total) * 100,
    unique: (adjustedChances.unique / total) * 100,
  }

  let cumulative = 0
  cumulative += normalized.common
  if (roll < cumulative) return "common"

  cumulative += normalized.rare
  if (roll < cumulative) return "rare"

  cumulative += normalized.epic
  if (roll < cumulative) return "epic"

  cumulative += normalized.legendary
  if (roll < cumulative) return "legendary"

  return "unique"
}

/**
 * Get a random item of a specific rarity
 */
export const getRandomItemByRarity = (rarity: ItemRarity): ItemDefinition | null => {
  const itemsByRarity = getItemsByRarity()
  const items = itemsByRarity[rarity]
  if (items.length === 0) {
    // Fallback to a lower rarity if none available
    const fallbackOrder: ItemRarity[] = ["legendary", "epic", "rare", "common"]
    for (const fallback of fallbackOrder) {
      if (itemsByRarity[fallback].length > 0) {
        return itemsByRarity[fallback][Math.floor(Math.random() * itemsByRarity[fallback].length)]
      }
    }
    return null
  }
  return items[Math.floor(Math.random() * items.length)]
}

/**
 * Roll for a random item based on rarity drop chances
 */
export const rollRandomItem = (rarityModifier: number = 1): ItemDefinition | null => {
  const rarity = rollItemRarity(rarityModifier)
  return getRandomItemByRarity(rarity)
}

// ============================================
// MAIN LOOT DROP FUNCTION
// ============================================

export interface LootDropResult {
  items: DroppedItem[]
  itemDefinitions: ItemDefinition[]
}

/**
 * Generate loot drops for a defeated enemy
 * @param enemyVariant The variant of the enemy (regular, elite, unique)
 * @param position The position where the enemy died
 * @param dropChanceModifier Optional modifier to adjust overall drop chances (e.g., 1.5 = 50% more likely to drop)
 * @param rarityModifier Optional modifier to adjust rarity chances (e.g., 1.5 = 50% better rarity)
 */
export const generateEnemyLoot = (
  enemyVariant: EnemyVariant,
  position: Position,
  dropChanceModifier: number = 1,
  rarityModifier: number = 1,
): LootDropResult => {
  const items: DroppedItem[] = []
  const droppedDefs: ItemDefinition[] = []

  // Calculate the base drop chance with modifier
  const baseDropChance = ENEMY_DROP_CHANCES[enemyVariant] * dropChanceModifier

  // Handle unique enemies specially - guaranteed legendary/unique drop
  if (enemyVariant === "unique") {
    // 100% drop a legendary or unique item
    const guaranteedItem = getRandomLegendaryOrUniqueItem()
    if (guaranteedItem) {
      const dropPos = calculateDropPosition(position, items.length)
      items.push(createDroppedItem(dropPos, guaranteedItem.id))
      droppedDefs.push(guaranteedItem)
    }

    // 50% chance for an additional random item
    if (Math.random() * 100 < UNIQUE_ADDITIONAL_DROP_CHANCE) {
      const additionalItem = rollRandomItem(rarityModifier)
      if (additionalItem) {
        const dropPos = calculateDropPosition(position, items.length)
        items.push(createDroppedItem(dropPos, additionalItem.id))
        droppedDefs.push(additionalItem)
      }
    }

    return { items, itemDefinitions: droppedDefs }
  }

  // Handle elite enemies
  if (enemyVariant === "elite") {
    // Check for main item drop (10% base)
    if (Math.random() * 100 < baseDropChance) {
      const item = rollRandomItem(rarityModifier)
      if (item) {
        const dropPos = calculateDropPosition(position, items.length)
        items.push(createDroppedItem(dropPos, item.id))
        droppedDefs.push(item)
      }
    }

    // Additional 33% chance to drop a potion
    if (Math.random() * 100 < ELITE_POTION_DROP_CHANCE) {
      const potion = getRandomPotion()
      if (potion) {
        const dropPos = calculateDropPosition(position, items.length)
        items.push(createDroppedItem(dropPos, potion.id))
        droppedDefs.push(potion)
      }
    }

    return { items, itemDefinitions: droppedDefs }
  }

  // Handle regular enemies (3% base drop chance)
  if (Math.random() * 100 < baseDropChance) {
    const item = rollRandomItem(rarityModifier)
    if (item) {
      const dropPos = calculateDropPosition(position, items.length)
      items.push(createDroppedItem(dropPos, item.id))
      droppedDefs.push(item)
    }
  }

  return { items, itemDefinitions: droppedDefs }
}

/**
 * Calculate drop position to avoid overlap when multiple items drop
 */
const calculateDropPosition = (basePosition: Position, itemIndex: number): Position => {
  if (itemIndex === 0) {
    return { ...basePosition }
  }

  // Arrange items in a small circle around the base position
  const angle = (itemIndex * Math.PI * 2) / 4 // Distribute in 4 directions
  const offset = ITEM_DROP_SPACING * itemIndex

  return {
    x: basePosition.x + Math.cos(angle) * offset,
    y: basePosition.y + Math.sin(angle) * offset,
  }
}
